/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type SimpleChanges, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract } from '@dm/core';
import { type Button } from '@dm/core/types';
import { type CIFSShare, type HomedirRule } from 'app/common/types/mo';

import { CifsShareStrService } from '../cifs-share-str.service';
import { AddHomedirRuleCifsShareComponent } from './add-homedir-rule-cifs-share/add-homedir-rule-cifs-share.component';
import { HomedirRuleCifsShareService } from './homedir-rule-cifs-share.service';
import { ModifyHomedirRuleCifsShareComponent } from './modify-homedir-rule-cifs-share/modify-homedir-rule-cifs-share.component';
import { RemoveHomedirRuleCifsShareComponent } from './remove-homedir-rule-cifs-share/remove-homedir-rule-cifs-share.component';

@Component({
    selector: 'app-homedir-rule-cifs-share',
    templateUrl: './homedir-rule-cifs-share.component.html',
    styleUrls: ['./homedir-rule-cifs-share.component.scss'],
})
export class HomedirRuleCifsShareComponent extends TableAbstract<HomedirRule> implements OnChanges {
    @Input() cifsShare?: CIFSShare;

    i18n = this.str.i18n;

    tableRest: Rest;
    tableCountRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: any[] = [];
    vstoreColumnIndex = 2;

    addBtn: Button;
    modifyBtn: Button;
    deleteBtn: Button;

    constructor(
        public str: CifsShareStrService,
        private service: HomedirRuleCifsShareService,
        private buttonService: ButtonService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((!changes.cifsShare.previousValue && changes.cifsShare.currentValue) ||
            (changes.cifsShare.previousValue && changes.cifsShare.currentValue &&
                changes.cifsShare.currentValue.ID !== changes.cifsShare.previousValue.ID)) {
            this.tableRest = this.service.getBatchRest(this.cifsShare.vstoreId, changes.cifsShare.currentValue.ID);
            this.tableCountRest = this.service.countRest(this.cifsShare.vstoreId, changes.cifsShare.currentValue.ID);
        }
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'create-homedir-rule-cifs-share',
            label: this.i18n.btn.addBtn,
            theme: 'primary',
            permission: ['share_W'],
            depPermission: ['file_system_R'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-homedir-rule-cifs-share',
            label: this.i18n.btn.modifyBtn,
            permission: ['share_W'],
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.deleteBtn = this.buttonService.create({
            id: 'delete-homedir-rule-cifs-share',
            label: this.i18n.btn.removeBtn,
            permission: ['share_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.delete(data ? [data] : this.selection);
            },
        });
    }


    initTable(): void {
        this.tableRest = this.service.getBatchRest(this.cifsShare.vstoreId, this.cifsShare.ID);
        this.tableCountRest = this.service.countRest(this.cifsShare.vstoreId, this.cifsShare.ID);

        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            pollingPeriod: 15,
            menuItems: [
                this.modifyBtn,
                this.deleteBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.label.userName,
                filterType: 'search',
            },
            {
                field: 'ID',
                header: this.i18n.label.id,
                hidden: true,
                columnFilter: true,
            },
            {
                field: 'fsPath',
                header: this.i18n.label.share_path,
                className: ['table-cell-text-pre'],
            },
            {
                field: 'priority',
                header: this.i18n.label.priority,
                sortable: true,
                columnFilter: true,
            },
            {
                field: 'autoCreate',
                header: this.i18n.label.autoCreate,
                columnFilter: true,
                render: (value) => value === 'true' ? this.i18n.label.enabled : this.i18n.label.disable,
            },
        ];
    }

    add(): void {
        this.doAction(AddHomedirRuleCifsShareComponent, {
            vstoreId: this.cifsShare.vstoreId,
            vstoreName: this.cifsShare.vstoreName,
            cifsShare: this.cifsShare,
        });
    }

    modify(data: CIFSShare): void {
        this.doAction(ModifyHomedirRuleCifsShareComponent, {
            data,
            vstoreId: this.cifsShare.vstoreId,
            vstoreName: this.cifsShare.vstoreName,
        });
    }

    delete(dataList: CIFSShare[]): void {
        this.doAction(RemoveHomedirRuleCifsShareComponent, { dataList });
    }
}
